import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, FileText, Mail, ArrowUp } from "lucide-react";

const LAST_UPDATED = "June 2026";

// ── Shared scaffolding ──────────────────────────────────────────────────────
function LegalShell({ kind, title, intro, sections }) {
  const { pathname, hash } = useLocation();

  // Smooth-scroll to deep-link anchors on mount
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);

  return (
    <div className="font-body bg-brand-bg" data-testid={`legal-${kind}`}>
      {/* Header band */}
      <section className="max-w-[1400px] mx-auto px-6 pt-16 pb-10">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-3 flex items-center gap-2">
          {kind === "privacy" ? <ShieldCheck className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
          {kind === "privacy" ? "Privacy" : "Terms of Use"}
        </div>
        <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-brand leading-[1.02]">
          {title}
        </h1>
        <p className="mt-4 text-[#5C5C59] text-base sm:text-lg max-w-3xl leading-relaxed">{intro}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider text-[#5C5C59] bg-white border border-brand-border rounded-full px-3 py-1.5">
            Last updated · {LAST_UPDATED}
          </span>
          <Link
            to={kind === "privacy" ? "/terms" : "/privacy"}
            className="inline-flex items-center gap-1.5 text-brand hover:text-brand-accent transition-colors font-medium"
            data-testid={kind === "privacy" ? "view-terms-link" : "view-privacy-link"}
          >
            {kind === "privacy" ? "View Terms of Use →" : "View Privacy Policy →"}
          </Link>
        </div>
      </section>

      {/* Two-column body: TOC + content */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* TOC (sticky on desktop) */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C59] mb-3">
              On this page
            </div>
            <ol className="space-y-2">
              {sections.map((s, i) => (
                <li key={s.id} className="text-sm">
                  <a
                    href={`#${s.id}`}
                    className="text-[#5C5C59] hover:text-brand-accent transition-colors flex gap-3"
                  >
                    <span className="font-mono text-[10px] text-brand-accent w-5 shrink-0 pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="leading-snug">{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 max-w-3xl">
          <div className="space-y-12">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono text-xs text-brand-accent tracking-[0.18em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-heading font-bold text-brand text-xl sm:text-2xl leading-tight">
                    {s.title}
                  </h2>
                </div>
                <div className="prose prose-sm sm:prose-base max-w-none text-[#111110] leading-relaxed">
                  {s.body}
                </div>
              </section>
            ))}
          </div>

          {/* Back to top + cross-link */}
          <div className="mt-16 pt-8 border-t border-brand-border flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex items-center gap-2 text-sm text-[#5C5C59] hover:text-brand transition-colors"
              data-testid="legal-back-to-top"
            >
              <ArrowUp className="h-4 w-4" /> Back to top
            </button>
            <a
              href="mailto:legal@nexintell.co"
              className="inline-flex items-center gap-2 text-sm text-brand hover:text-brand-accent transition-colors font-medium"
            >
              <Mail className="h-4 w-4" /> legal@nexintell.co
            </a>
          </div>
        </article>
      </section>
    </div>
  );
}

// ── Reusable text helpers ───────────────────────────────────────────────────
const P = ({ children }) => (
  <p className="text-[#111110] text-[15px] leading-[1.75]">{children}</p>
);
const Lead = ({ label, children }) => (
  <p className="text-[#111110] text-[15px] leading-[1.75]">
    <span className="font-heading font-semibold text-brand">{label}: </span>
    {children}
  </p>
);
const UL = ({ children }) => (
  <ul className="mt-3 space-y-2 text-[#111110] text-[15px] leading-[1.7] list-disc pl-5 marker:text-brand-accent">
    {children}
  </ul>
);

// ── PRIVACY POLICY ──────────────────────────────────────────────────────────
const PRIVACY_SECTIONS = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <P>
          Infralytics-GIIS is a platform operated by <span className="font-medium">Nexintell Limited</span>,
          registered in England and Wales (Company No. 16958958), with its registered office in
          Stockton-on-Tees, North East England.
        </P>
        <P>
          For all data privacy enquiries, contact{" "}
          <a className="text-brand-accent hover:underline" href="mailto:privacy@nexintell.co">
            privacy@nexintell.co
          </a>.
        </P>
      </>
    ),
  },
  {
    id: "data-we-collect",
    title: "What data we collect",
    body: (
      <>
        <P>
          When you use the Infralytics-GIIS platform, we may collect the following categories of personal data:
        </P>
        <div className="mt-4 space-y-3">
          <Lead label="Contact and Identity Data">
            Name, email address, job title, and organisation name — collected when you submit a waitlist
            registration, partnership request, or contributor application.
          </Lead>
          <Lead label="Usage Data">
            Information about how you interact with the platform, including pages visited, features
            accessed, and session duration — collected automatically via analytics tools.
          </Lead>
          <Lead label="Form Submission Data">
            Any information you voluntarily provide through platform forms, including areas of interest,
            organisational context, and described expertise.
          </Lead>
        </div>
        <P>
          <span className="font-medium">We do not collect</span> payment card data, government
          identification, or sensitive personal data as defined under UK GDPR.
        </P>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How we use your data",
    body: (
      <>
        <P>We use the data we collect to:</P>
        <UL>
          <li>Process and respond to partnership and contributor requests</li>
          <li>Communicate platform updates, working group information, and pilot access opportunities</li>
          <li>Understand how the platform is being used in order to improve it</li>
          <li>Comply with legal obligations</li>
        </UL>
        <P>
          <span className="font-medium">We do not sell your data.</span> We do not share your data with
          third parties for marketing purposes.
        </P>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "Legal basis for processing",
    body: (
      <>
        <P>We process your personal data on the following legal bases under UK GDPR:</P>
        <UL>
          <li><span className="font-medium">Legitimate interests</span> — to operate and improve the platform</li>
          <li><span className="font-medium">Consent</span> — where you have explicitly agreed to receive communications</li>
          <li><span className="font-medium">Contract</span> — where processing is necessary to fulfil a partnership or contributor agreement</li>
        </UL>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data retention",
    body: (
      <P>
        We retain personal data only for as long as necessary for the purposes described above. Contact
        and form submission data is retained for a maximum of <span className="font-medium">24 months</span>,
        after which it is securely deleted unless an ongoing relationship requires otherwise.
      </P>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <P>Under UK GDPR, you have the right to:</P>
        <UL>
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Object to or restrict processing</li>
          <li>Withdraw consent at any time (where consent is the legal basis)</li>
          <li>
            Lodge a complaint with the Information Commissioner&apos;s Office (ICO) at{" "}
            <a className="text-brand-accent hover:underline" href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>
          </li>
        </UL>
        <P>
          To exercise any of these rights, contact{" "}
          <a className="text-brand-accent hover:underline" href="mailto:privacy@nexintell.co">
            privacy@nexintell.co
          </a>.
        </P>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and analytics",
    body: (
      <P>
        The Infralytics-GIIS platform uses cookies and similar technologies to support basic site
        functionality and collect anonymised usage data. You may adjust your cookie preferences at any
        time through your browser settings.
      </P>
    ),
  },
];

export function PrivacyPolicy() {
  return (
    <LegalShell
      kind="privacy"
      title="Privacy Policy"
      intro="How Nexintell Limited collects, uses, and protects personal data through the Infralytics-GIIS platform — written in plain English, aligned with UK GDPR."
      sections={PRIVACY_SECTIONS}
    />
  );
}

// ── TERMS OF USE ────────────────────────────────────────────────────────────
const TERMS_SECTIONS = [
  {
    id: "acceptance",
    title: "Acceptance",
    body: (
      <P>
        By accessing or using the Infralytics-GIIS platform, you agree to these Terms of Use. If you do
        not agree, please do not use the platform.
      </P>
    ),
  },
  {
    id: "platform-access",
    title: "Platform access",
    body: (
      <>
        <P>
          Infralytics-GIIS is provided for informational, research, and partnership engagement purposes.
          The platform and its contents — including infrastructure indices, methodology, and analytical
          outputs — are the intellectual property of Nexintell Limited and are protected under applicable
          UK and international intellectual property law.
        </P>
        <P>
          The <span className="font-medium">Infralytics®</span> trademark and the Infralytics-GIIS brand
          are registered or pending registration. Unauthorised use is prohibited.
        </P>
      </>
    ),
  },
  {
    id: "data-methodology",
    title: "Data and methodology",
    body: (
      <>
        <P>
          Infrastructure data displayed on the Infralytics-GIIS platform is derived from Ofcom Connected
          Nations datasets published under the{" "}
          <span className="font-medium">Open Government Licence v3.0</span>. Original indices, scoring
          methodologies, and derived datasets — including the Full Fibre Deployment Density Index
          (FDDI), the Connectivity Equity Index (CEI), the Business Digital Adoption Footprint, and the
          Meaningful Connectivity Index Framework (MCIF) — are original works of Nexintell Limited.
        </P>
        <P>
          The peer-archived methodology is available at{" "}
          <a
            className="text-brand-accent hover:underline"
            href="https://doi.org/10.5281/zenodo.18796078"
            target="_blank"
            rel="noopener noreferrer"
          >
            DOI 10.5281/zenodo.18796078
          </a>.
        </P>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <P>You may not use the platform to:</P>
        <UL>
          <li>Extract, scrape, or reproduce data or methodology without written permission</li>
          <li>Misrepresent the source or ownership of platform outputs</li>
          <li>Attempt to gain unauthorised access to platform systems or administrative functions</li>
          <li>Use platform outputs for commercial purposes without a valid licence agreement</li>
        </UL>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <P>
        The platform is provided in good faith and based on the best available data. Nexintell Limited
        does not warrant that outputs are error-free or suitable for any specific regulatory or legal
        purpose. We accept no liability for decisions made on the basis of platform intelligence
        without independent professional verification.
      </P>
    ),
  },
  {
    id: "governing-law",
    title: "Governing law",
    body: (
      <P>
        These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the
        exclusive jurisdiction of the courts of England and Wales.
      </P>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <P>For any queries relating to these Terms or our Privacy Policy:</P>
        <div className="mt-3 bg-white border border-brand-border rounded-md p-5 text-[15px] leading-[1.8]">
          <div className="font-heading font-semibold text-brand">Nexintell Limited</div>
          <div className="text-[#5C5C59]">Stockton-on-Tees, North East England</div>
          <a className="text-brand-accent hover:underline block mt-1" href="mailto:legal@nexintell.co">
            legal@nexintell.co
          </a>
          <a
            className="text-brand-accent hover:underline block"
            href="https://www.nexintell.co"
            target="_blank"
            rel="noopener noreferrer"
          >
            nexintell.co
          </a>
        </div>
      </>
    ),
  },
];

export function TermsOfUse() {
  return (
    <LegalShell
      kind="terms"
      title="Terms of Use"
      intro="The rules of engagement for accessing the Infralytics-GIIS platform — built on UK and international intellectual property law and the Open Government Licence v3.0."
      sections={TERMS_SECTIONS}
    />
  );
}

export default PrivacyPolicy;
